import NextImage, { ImageProps } from 'next/image';
import { useState } from 'react';

import clsxm from '@/lib/clsxm';
import { Maybe } from '@/lib/types';
import useAnimateOnVisible from '@/hooks/use-animate-on-visible';
import { useEagerImageContext } from '@/hooks/use-eager-image-context';

import COMMON_CLASS_NAMES from '@/constants/common-class-names';
import { hygraphImageLoader } from '@/utils/hygraph-utils';

export type HygraphImageProps = {
  url: string;
  description?: Maybe<string> | undefined;
  width?: Maybe<number> | string;
  height?: Maybe<number> | string;
  layout?: 'fill' | 'fixed' | 'intrinsic' | 'responsive' | undefined;
  loading?: 'lazy' | 'eager' | undefined;
  className?: Maybe<string> | undefined;
  imgClassName?: Maybe<string> | undefined;
  containerClassName?: Maybe<string> | undefined;
  sizes?: Maybe<string> | undefined;
  priority?: Maybe<boolean>;
  fill?: boolean;
  animateOnVisible?: boolean;
  animateDelay?: number;
  roundedCorners?: boolean;
  quality?: number;
  scaleOnHover?: boolean;
  unoptimized?: boolean;
  draggable?: boolean;
};

const HygraphImage: React.FC<HygraphImageProps> = ({
  url,
  description,
  width,
  height,
  loading,
  className,
  containerClassName,
  imgClassName,
  sizes,
  priority,
  fill,
  animateOnVisible,
  animateDelay,
  roundedCorners,
  quality,
  scaleOnHover,
  unoptimized,
  draggable,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const isEager = useEagerImageContext();

  const { ref, animateClassName } = useAnimateOnVisible({
    delay: animateDelay,
    wait: !animateOnVisible || !isLoaded,
  });

  if (!url) {
    return <></>;
  }

  const imgProps: ImageProps = {
    src: url,
    alt: description ?? '',
    loader: hygraphImageLoader,
    loading: isEager ? 'eager' : (loading ?? 'lazy'),
    className: clsxm(
      scaleOnHover && COMMON_CLASS_NAMES.scaleOnHover,
      roundedCorners && 'rounded-xl',
      imgClassName
    ),
    sizes: sizes ?? '100vw',
    priority: !!priority,
    fill: !!fill,
    quality,
    draggable,
  };
  if (!imgProps.fill) {
    if (width && !Number.isNaN(width)) {
      imgProps.width = Number(width);
    }
    if (height && !Number.isNaN(height)) {
      imgProps.height = Number(height);
    }
  }

  if (!width && !height) {
    imgProps.fill = true;
  }

  if (unoptimized) {
    imgProps.unoptimized = unoptimized;
  }

  return (
    <div
      ref={ref}
      className={clsxm(
        className,
        containerClassName,
        animateOnVisible && animateClassName,
        roundedCorners && 'overflow-hidden rounded-xl'
      )}
    >
      <NextImage onLoad={() => setIsLoaded(true)} {...imgProps} />
    </div>
  );
};

export default HygraphImage;
